import { QueryClient } from '@tanstack/react-query';
import { ApiRequestError } from './api-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Socket events drive freshness for queue data, so aggressive polling would
      // be redundant work. Non-realtime lists get a short stale window instead.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError) {
          // A 4xx will not become a 2xx by asking again.
          if (error.statusCode >= 400 && error.statusCode < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  session: ['session'] as const,
  clinic: ['clinic'] as const,
  doctors: (includeInactive?: boolean) => ['doctors', { includeInactive }] as const,
  doctorBriefs: ['doctors', 'briefs'] as const,
  doctor: (id: string) => ['doctors', id] as const,
  chambers: ['chambers'] as const,
  patients: (params: Record<string, unknown>) => ['patients', params] as const,
  patient: (id: string) => ['patients', id] as const,
  appointments: (params: Record<string, unknown>) => ['appointments', params] as const,
  appointment: (id: string) => ['appointments', id] as const,
  queue: (doctorId: string, date?: string) => ['queue', doctorId, date ?? 'today'] as const,
  queueBoard: (date?: string) => ['queues', 'board', date ?? 'today'] as const,
  displayQueue: (doctorId: string) => ['display', 'queue', doctorId] as const,
  displayChamberQueue: (chamberId: string) => ['display', 'chamberQueue', chamberId] as const,
  chamberActiveDoctor: (chamberId: string) => ['chambers', 'activeDoctor', chamberId] as const,
  chamberOccupancy: ['chambers', 'occupancy'] as const,
  publicChambers: (clinicCode: string) => ['chambers', 'public', clinicCode] as const,
  publicDoctors: (clinicCode: string) => ['doctors', 'public', clinicCode] as const,
  myChamberCheckIn: ['chambers', 'myCheckIn'] as const,
  displayConfig: ['display', 'config'] as const,
  schedules: (doctorId: string) => ['schedules', doctorId] as const,
  availability: (doctorId: string, date: string) => ['availability', doctorId, date] as const,
  users: (params: Record<string, unknown>) => ['users', params] as const,
  reports: (name: string, params: Record<string, unknown>) => ['reports', name, params] as const,
  auditLogs: (params: Record<string, unknown>) => ['audit-logs', params] as const,
  displaySessions: ['display', 'sessions'] as const,
  consultation: (id: string) => ['consultations', id] as const,
  consultationByToken: (tokenId: string) => ['consultations', 'by-token', tokenId] as const,
  consultationHistory: (patientId: string) => ['consultations', 'history', patientId] as const,
  dictationCapability: ['consultations', 'dictation', 'capability'] as const,
} as const;
